import {
  transport as generatedTransport,
  transportOperationDescriptors as generatedTransportOperationDescriptors,
  transportOperations as generatedTransportOperations,
  getTransportOperationName,
  getTransportOperationVersion,
  type TransportOperationInput,
  type TransportOperationName,
  type TransportOperationResponse,
} from "@/types_api/backend/transport/operations"

import type {
  createTransportClient,
  TransportCallExecutionOptions,
} from "../utils/transportClient"
import type {
  TransportResolvedRequest,
  TransportRuntimeVersion,
  TransportVersionedOperationGroup,
} from "./transportQueryTypes"

export const transport = generatedTransport
export const transportOperationDescriptors =
  generatedTransportOperationDescriptors
export const transportOperations = generatedTransportOperations

export function isTransportOperationReady({
  authRequired,
  isAuthLoaded,
  isOrganizationSynced,
  organizationRequired,
  organizationSlug,
}: {
  authRequired: boolean
  isAuthLoaded: boolean
  isOrganizationSynced: boolean
  organizationRequired: boolean
  organizationSlug: string | null
}) {
  return (
    (!authRequired || isAuthLoaded) &&
    (!organizationRequired ||
      (isAuthLoaded && isOrganizationSynced && organizationSlug !== null))
  )
}

export async function executeTransportOperation<
  Name extends TransportOperationName,
>(
  client: ReturnType<typeof createTransportClient>,
  operation: Name,
  request?: TransportResolvedRequest<Name>,
  options?: TransportCallExecutionOptions,
): Promise<TransportOperationResponse<Name>> {
  if (request === undefined) {
    const operationFn = client.operations[operation] as (
      options?: TransportCallExecutionOptions,
    ) => Promise<TransportOperationResponse<Name>>
    return options === undefined ? operationFn() : operationFn(options)
  }

  const operationFn = client.operations[operation] as (
    request: TransportResolvedRequest<Name>,
    options?: TransportCallExecutionOptions,
  ) => Promise<TransportOperationResponse<Name>>

  return options === undefined
    ? operationFn(request)
    : operationFn(request, options)
}

export function resolveTransportOperationName(params: {
  operation: TransportOperationInput<TransportOperationName>
  version?: TransportRuntimeVersion
}): TransportOperationName {
  const operation = getTransportOperationName(params.operation)
  if (!("version" in params) || params.version === undefined) {
    return operation
  }

  return getTransportOperationVersion(
    operation as TransportVersionedOperationGroup,
    params.version,
  ).name
}
